import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { bookingService } from '@/services/api/booking.service';

interface EditBatchDialogProps {
  seriesId: string;
  // Scope the edit to one participant's bookings in the series. The dialog is
  // always opened from a single participant's context (booking page or a
  // participant row), so this is required.
  customerEmail: string;
  // Number of upcoming bookings the edit will touch (this participant's count).
  count: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

// Batch-edit a participant's upcoming bookings in a series: append a note
// and/or confirm any still-pending ones. Both are optional but at least one is
// required to submit.
export function EditBatchDialog({ seriesId, customerEmail, count, open, onOpenChange, onSuccess }: EditBatchDialogProps) {
  const { t } = useTranslation(['bookings', 'common']);

  const [note, setNote] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);

  useEffect(() => {
    if (open) {
      setNote('');
      setConfirmAll(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      bookingService.updateSeries(seriesId, {
        notes: note.trim() || undefined,
        status: confirmAll ? 'confirmed' : undefined,
        customerEmail,
      }),
    onSuccess: (result) => {
      toast.success(
        t('bookings:batch.editSuccess', {
          defaultValue: 'Series updated: {{notes}} note(s) added, {{confirmed}} confirmed',
          notes: result.notes_updated_count,
          confirmed: result.confirmed_count,
        }),
      );
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message;
      toast.error(message || t('bookings:batch.editError', 'Failed to update series'));
    },
  });

  const nothingToDo = !note.trim() && !confirmAll;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('bookings:batch.editTitle', 'Edit batch')}</DialogTitle>
          <DialogDescription>
            {t('bookings:batch.editDescription', {
              defaultValue: 'Applies to all {{count}} upcoming bookings in this batch.',
              count,
            })}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!nothingToDo) mutation.mutate();
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="batch_note" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('bookings:batch.appendNote', 'Append a note to every booking')}
            </Label>
            <Textarea
              id="batch_note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('bookings:batch.appendNotePlaceholder', 'Added to existing notes, not replacing them')}
            />
          </div>

          <label className="flex items-start gap-3 rounded-lg border border-line p-3 cursor-pointer">
            <Checkbox
              checked={confirmAll}
              onCheckedChange={(v) => setConfirmAll(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm text-ink">
              {t('bookings:batch.confirmAll', 'Confirm all pending bookings in this batch')}
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending || nothingToDo}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('bookings:edit.saving', 'Saving...')}
                </>
              ) : (
                t('bookings:edit.save', 'Save Changes')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
